import {
  Children,
  cloneElement,
  isValidElement,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { withErrorBoundary } from "@sentry/nextjs";
import styled from "styled-components";

import ErrorFallback from "components/fallbacks/error";
import { darkMode } from "components/map/utils";

const MAX_ZOOM = 20;
const AUTO_ZOOM = false;

export const dottedLines = [
  {
    icon: {
      path: "M 0,-1 0,1",
      strokeOpacity: 1,
      scale: 4,
    },
    offset: "10px",
    repeat: "20px",
  },
];

const Container = styled.div`
  width: 100%;
  height: 100vh;

  .infoWindow {
    color: #2a2f39 !important;
  }

  @media only screen and (max-width: 768px) {
    width: 100%;
    height: calc(100vh - auto);
  }
`;

const Map = ({
  center,
  zoom,
  maxZoom,
  autoZoom,
  children,
  isDarkMode,
  bounds,
  resetMap,
  onCenterChange,
}: {
  center: google.maps.LatLngLiteral;
  zoom: number;
  maxZoom?: number | null;
  autoZoom?: boolean;
  children: any;
  isDarkMode: boolean;
  bounds: google.maps.LatLngLiteral[];
  resetMap: MutableRefObject<() => void>;
  onCenterChange: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [isBoundsFitted, setIsBoundsFitted] = useState<boolean>(false);
  let ctrlPosition = google.maps.ControlPosition.RIGHT_BOTTOM;
  let hideStreetView = false;

  const fitBounds = useCallback(() => {
    if ((map && !isBoundsFitted) || autoZoom || AUTO_ZOOM) {
      const gBounds = new google.maps.LatLngBounds();
      bounds.map((bound) => {
        gBounds.extend(bound);
      });
      if (window.innerWidth < 768) {
        map?.fitBounds(gBounds, { bottom: 350, top: 40 });
      } else {
        map?.fitBounds(gBounds, { bottom: 220, top: 20 });
      }
      setIsBoundsFitted(true);
    }
  }, [autoZoom, bounds, isBoundsFitted, map]);

  useEffect(() => {
    // reset maps to fit bounds
    resetMap.current = () => setIsBoundsFitted(false);
  }, [resetMap]);

  useEffect(() => {
    if (ref.current && !map) {
      setMap(
        new window.google.maps.Map(ref.current, {
          maxZoom: maxZoom || MAX_ZOOM,
          mapTypeControl: false,
          zoomControlOptions: {
            position: ctrlPosition,
          },
          streetViewControl: !hideStreetView,
          streetViewControlOptions: {
            position: ctrlPosition,
          },
          fullscreenControl: false,
          center,
          zoom,
        })
      );
    }
    // fit bound only once when map is initialized and is loaded also: override is auto zoom is true
    fitBounds();
    map?.setOptions({ styles: isDarkMode && darkMode });
    if (!autoZoom) {
      map?.addListener("dragend", () => {
        onCenterChange();
      });
    }
  }, [
    ref,
    map,
    center,
    autoZoom,
    zoom,
    onCenterChange,
    bounds,
    isDarkMode,
    isBoundsFitted,
    fitBounds,
    ctrlPosition,
    hideStreetView,
    maxZoom,
  ]);

  return (
    <>
      <Container ref={ref} />
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          // @ts-ignore
          return cloneElement(child, { map });
        }
      })}
    </>
  );
};

export default withErrorBoundary(Map, { fallback: ErrorFallback });
